import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import type { HttpTypes } from "@medusajs/types";
import sdk from "@repo/api-sdk";
import { analytics } from "@/lib/analytics";
import { getCapabilityToken } from "@/lib/concierge/capability-token";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import {
  invalidateCartShippingOptions,
  invalidateSellerCarts,
  removeLineItem,
  rollbackSellerCarts,
  setCartShippingMethod,
  setLineItemQuantity,
  snapshotSellerCarts,
  type SellerCartsMutationContext
} from "./cache";
import { queryKeys } from "./queryKeys";

/**
 * How long a burst of quantity edits is allowed to keep collecting before it
 * is sent. Long enough that holding "+" or typing a two-digit number is one
 * request, short enough that a single deliberate click still feels committed.
 */
const QUANTITY_COMMIT_DELAY_MS = 400;

/**
 * How long after a quantity write to pull the server's restated figures.
 *
 * Longer than the commit delay on purpose. The cache patch already moves every
 * number on screen, so the refetch exists only to reconcile what the client
 * can't derive - tax, discounts, and a shipping quote that may have crossed a
 * free-shipping threshold. Someone still working the stepper would otherwise
 * have those recalculations landing under them mid-adjustment; waiting until
 * they've settled means one reconciliation instead of one per edit.
 */
const QUANTITY_REFRESH_DELAY_MS = 2000;

/**
 * Input for adding an item to a seller cart.
 * The seller is determined from the offer_id.
 */
type AddToSellerCartInput = Pick<
  SalonStockerHttpTypes.StoreAddToSellerCart,
  "offer_id" | "quantity" | "metadata"
> & {
  /** Client-only analytics flag (never sent to the API): this add is the first
   *  half of a Quick Buy that goes straight to checkout. */
  quickBuy?: boolean;
};

/**
 * Adds an item to the appropriate seller cart with optimistic updates.
 * Creates a new cart automatically if one doesn't exist for that seller.
 *
 * @example
 * ```tsx
 * const { mutateAsync: addToCart, isPending } = useAddToSellerCart();
 *
 * const handleAdd = async () => {
 *   await addToCart({ offer_id: "soff_123", quantity: 2 });
 *   toast.success("Added to cart!");
 * };
 * ```
 */
export function useAddToSellerCart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quickBuy: _quickBuy, ...body }: AddToSellerCartInput) =>
      sdk.store.sellerCart.addItem(body),

    // Funnel step 4, instrumented here rather than at the call sites so every
    // manual add is captured wherever it originates. The bulk paths - reorder,
    // saved lists, stock reorder - never reach this mutation; each has its own
    // decision event carrying counts, which is why manual single-item adds were
    // the one part of the basket nobody could see.
    //
    // `onSuccess`, not `onSettled`: a failed add is not an add.
    onSuccess: (data, variables) => {
      analytics.addToCart({
        offer_id: variables.offer_id,
        seller_id: data.seller_id,
        quantity: variables.quantity,
        is_new_cart: data.is_new_cart,
        quick_buy: variables.quickBuy ?? false
      });
    },

    // No optimistic patch is possible (a new line item can't be fabricated
    // client-side), so there's nothing to snapshot or roll back - a failed add
    // never touched the cache. Just reconcile from server truth on settle.
    onSettled: (data) => {
      // The cart id isn't known until the response: the seller's cart is
      // resolved (or created) from the offer.
      invalidateCartShippingOptions(queryClient, data?.cart_id);
      return invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Adds an ad hoc supplier item - a hand-typed, fixed-price line Caroline
 * captured directly from a supplier's Shopify storefront (KAN-193) - to a
 * seller cart.
 *
 * Requires a concierge operator's capability token to already be stored (see
 * `lib/concierge/capability-token.ts`, delivered via the `/enter` `cap`
 * param). Read fresh at call time and sent as a per-call header - never added
 * to the SDK's global headers - so it only ever reaches this one call.
 * Rejects up front, before any network request, when no token is stored: an
 * ad hoc add attempted without one can never succeed server-side, and failing
 * fast here gives the paste-in form (Pass II) an immediate, specific error
 * instead of a generic 401 round trip.
 *
 * No optimistic patch, same reasoning as {@link useAddToSellerCart}: whether
 * this merges into an existing ad hoc line or starts a new one is resolved
 * server-side, so there is nothing to fabricate client-side. Settling always
 * reconciles from server truth via the same invalidation {@link
 * useAddToSellerCart} uses.
 *
 * @example
 * ```tsx
 * const { mutateAsync: addAdHocItem, isPending } = useAddAdHocSellerCartItem();
 *
 * const handleAdd = async () => {
 *   await addAdHocItem({
 *     seller_id: "sel_123",
 *     shopify_variant_id: "40287744385",
 *     shopify_product_id: "8123456789",
 *     title: "Blonde Toner 500ml",
 *     unit_price: 42.5,
 *     quantity: 1
 *   });
 *   toast.success("Added to cart!");
 * };
 * ```
 */
export function useAddAdHocSellerCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SalonStockerHttpTypes.StoreAddAdHocLineItem) => {
      const token = getCapabilityToken();
      if (!token) {
        return Promise.reject(
          new Error(
            "This session has no concierge capability token, so it cannot add ad hoc items."
          )
        );
      }

      return sdk.store.sellerCart.addAdHocItem(data, {
        "x-concierge-capability-token": token
      });
    },

    // No optimistic patch is possible here either (same reasoning as
    // useAddToSellerCart's own onSettled) - just reconcile from server truth.
    onSettled: (data) => {
      invalidateCartShippingOptions(queryClient, data?.cart.cart_id);
      return invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Updates line item quantity with optimistic update.
 *
 * Prefer {@link UseUpdateCartItemResult.setQuantity} over `mutate` for
 * anything driven by a stepper or a text input - see its docs. `mutate` /
 * `mutateAsync` remain for one-shot programmatic updates that should go out
 * immediately.
 *
 * @example
 * ```tsx
 * const { setQuantity } = useUpdateCartItem(cartId);
 *
 * setQuantity("item_123", 3);
 * ```
 */
export function useUpdateCartItem(cartId: string) {
  const queryClient = useQueryClient();

  // The cache state from before the first patch of the current burst. A burst
  // of clicks patches the cache repeatedly but sends one request, so the
  // mutation's own snapshot would capture a cache that ALREADY contains the
  // edits it might have to undo. Rolling back to this instead undoes the
  // whole burst, which is what the buyer would expect from one failure.
  const burstSnapshot = useRef<SellerCartsMutationContext | undefined>(
    undefined
  );
  // Per line item: the pending commit timer, and the latest quantity for it.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const latest = useRef(new Map<string, number>());
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  /** Pull the server's restated totals, once the edits have settled. */
  const refresh = useCallback(() => {
    // A quantity change moves the subtotal, which can cross a free-shipping
    // threshold and reprice this cart's options - but only this cart's.
    invalidateCartShippingOptions(queryClient, cartId);
    invalidateSellerCarts(queryClient);
  }, [cartId, queryClient]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = undefined;
      refreshRef.current();
    }, QUANTITY_REFRESH_DELAY_MS);
  }, []);

  const mutation = useMutation({
    mutationFn: ({
      lineItemId,
      quantity
    }: {
      lineItemId: string;
      quantity: number;
    }) => sdk.store.sellerCart.updateLineItem(cartId, lineItemId, { quantity }),

    onMutate: async ({ lineItemId, quantity }) => {
      // Still cancels in-flight refetches and patches the cache, so a direct
      // `mutate` caller keeps the optimistic behaviour it always had. When a
      // burst is in progress the patch is simply a no-op re-write of the value
      // `setQuantity` already applied.
      const context = await snapshotSellerCarts(queryClient);
      queryClient.setQueryData<SalonStockerHttpTypes.StoreSellerCartsResponse>(
        queryKeys.sellerCarts.all,
        (old) =>
          old ? setLineItemQuantity(old, cartId, lineItemId, quantity) : old
      );
      return burstSnapshot.current ?? context;
    },

    onError: (_err, _vars, context) =>
      rollbackSellerCarts(queryClient, context),

    onSettled: () => {
      burstSnapshot.current = undefined;
      scheduleRefresh();
    }
  });

  // Read through a ref so the callbacks below, and the unmount flush, can stay
  // mount-scoped instead of re-registering on every render.
  const { mutateAsync } = mutation;
  const mutateAsyncRef = useRef(mutateAsync);
  mutateAsyncRef.current = mutateAsync;

  // Per line item: the request a committed burst is waiting on, so `flush` can
  // outlast it - and only it, not a different line's.
  const inFlight = useRef(new Map<string, Promise<unknown>>());

  /**
   * Send the quantity waiting for a line item (if any) now, dropping its
   * debounce timer, and remember the request for {@link flush}. The one place
   * a burst is committed: the timer, `flush` and the unmount cleanup all end
   * here.
   */
  const commitPending = useCallback((lineItemId: string) => {
    const timer = timers.current.get(lineItemId);
    if (timer) clearTimeout(timer);
    timers.current.delete(lineItemId);

    const quantity = latest.current.get(lineItemId);
    latest.current.delete(lineItemId);
    if (quantity === undefined) return;

    // The mutation's own `onError` has already rolled the cache back, so the
    // rejection carries nothing left to handle here.
    const request = mutateAsyncRef
      .current({ lineItemId, quantity })
      .catch(() => undefined)
      .then(() => {
        if (inFlight.current.get(lineItemId) === request) {
          inFlight.current.delete(lineItemId);
        }
      });
    inFlight.current.set(lineItemId, request);
  }, []);

  /**
   * Set a line item's quantity: patches the cache immediately, sends one
   * request once the edits stop.
   *
   * The whole page moves on the click - stepper, line total and the summary
   * rail all read the same patched cache - while four rapid clicks collapse
   * into a single PATCH instead of four round trips (each of which previously
   * also locked the row, so the later clicks were dropped rather than queued).
   */
  const setQuantity = useCallback(
    (lineItemId: string, quantity: number) => {
      if (!burstSnapshot.current) {
        burstSnapshot.current = {
          previousCarts:
            queryClient.getQueryData<SalonStockerHttpTypes.StoreSellerCartsResponse>(
              queryKeys.sellerCarts.all
            )
        };
      }

      queryClient.setQueryData<SalonStockerHttpTypes.StoreSellerCartsResponse>(
        queryKeys.sellerCarts.all,
        (old) =>
          old ? setLineItemQuantity(old, cartId, lineItemId, quantity) : old
      );

      latest.current.set(lineItemId, quantity);
      const existing = timers.current.get(lineItemId);
      if (existing) clearTimeout(existing);
      timers.current.set(
        lineItemId,
        setTimeout(() => commitPending(lineItemId), QUANTITY_COMMIT_DELAY_MS)
      );
    },
    [cartId, queryClient, commitPending]
  );

  /**
   * Resolve once every quantity edit for this line has reached the server:
   * sends a burst that is still collecting immediately instead of waiting out
   * the debounce, then waits for the request in flight.
   *
   * For an action that must not run against a stale line - switching its
   * variant reads the quantity server-side, so it has to come after the edit
   * the buyer can already see on screen. Never rejects: a failed edit has
   * already rolled the cache back, and the caller's own action is what the
   * buyer asked for next.
   */
  const flush = useCallback(
    async (lineItemId: string) => {
      commitPending(lineItemId);
      await inFlight.current.get(lineItemId);
    },
    [commitPending]
  );

  // Commit anything still waiting when the component goes away - navigating
  // off the cart mid-burst must not silently discard the edit - and drop the
  // deferred refresh, marking the cart stale immediately instead. Nothing is
  // rendering it any more, so there is no point protecting it from a refetch.
  useEffect(() => {
    const pendingTimers = timers.current;
    return () => {
      // Copied: committing a line removes it from the map being walked.
      for (const lineItemId of [...pendingTimers.keys()]) {
        commitPending(lineItemId);
      }

      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = undefined;
        refreshRef.current();
      }
    };
  }, [commitPending]);

  return { ...mutation, setQuantity, flush };
}

/**
 * Removes line item with optimistic update.
 *
 * @example
 * ```tsx
 * const removeItem = useRemoveCartItem(cartId);
 *
 * removeItem.mutate("item_123");
 * ```
 */
export function useRemoveCartItem(cartId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lineItemId: string) =>
      sdk.store.sellerCart.deleteLineItem(cartId, lineItemId),

    onMutate: async (lineItemId) => {
      const context = await snapshotSellerCarts(queryClient);
      queryClient.setQueryData<SalonStockerHttpTypes.StoreSellerCartsResponse>(
        queryKeys.sellerCarts.all,
        (old) => (old ? removeLineItem(old, cartId, lineItemId) : old)
      );
      return context;
    },

    onError: (_err, _vars, context) =>
      rollbackSellerCarts(queryClient, context),

    onSettled: () => {
      // Removing an item drops the subtotal, which can un-qualify this cart
      // from a free-shipping tier and reprice its options.
      invalidateCartShippingOptions(queryClient, cartId);
      return invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Switches a cart line to another variant of the same product from the same
 * supplier.
 *
 * No optimistic patch: the new variant brings its own price, the line may
 * merge into one the cart already holds (so the line, and its id, can
 * disappear), and none of that can be derived client-side. The mutation stays
 * pending until the cart refetch has landed - `onSettled` returns the
 * invalidation - so the row stays locked until it shows the new variant
 * instead of flashing the old one back.
 *
 * Callers should `flush` {@link useUpdateCartItem} first: the server reads the
 * line's quantity when it switches, so a quantity edit still waiting on its
 * debounce would otherwise be applied to a line that no longer exists.
 *
 * @example
 * ```tsx
 * const changeVariant = useChangeCartItemVariant(cartId);
 *
 * await changeVariant.mutateAsync({
 *   lineItemId: "item_123",
 *   variant: { offer_id: "soff_456" }
 * });
 * ```
 */
export function useChangeCartItemVariant(cartId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lineItemId,
      variant
    }: {
      lineItemId: string;
      /** `{ offer_id }` for an offer line, `{ shopify_variant_id }` for an ad hoc one. */
      variant: SalonStockerHttpTypes.StoreChangeLineItemVariant;
    }) =>
      sdk.store.sellerCart.changeLineItemVariant(cartId, lineItemId, variant),

    onSettled: () => {
      // A different variant is a different price, which can cross a
      // free-shipping threshold.
      invalidateCartShippingOptions(queryClient, cartId);
      return invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Key of a cart's add-shipping-method mutation, so a consumer that did not
 * start one can still see it is in flight (`queryClient.isMutating`).
 */
export function addShippingMethodMutationKey(cartId: string) {
  return ["seller-carts", cartId, "add-shipping-method"] as const;
}

/** The chosen option, carried so the cache can be patched before the round trip. */
export interface AddCartShippingMethodInput {
  optionId: string;
  /** The option's display name, as listed. */
  name: string;
  /** The option's list price, as listed. */
  amount: number;
}

/**
 * Adds a shipping method to a cart, with an optimistic update.
 *
 * The selector's value is read from cart data, so before this patch existed a
 * click waited on the POST *and* the follow-up cart refetch - two serial round
 * trips - while the dropdown still showed the previous method. It takes the
 * option's name and price alongside its id purely so the patched record is
 * complete enough to render.
 *
 * Shipping options are deliberately not invalidated here: choosing a method
 * changes which option is selected, not which options exist or what they cost.
 *
 * @example
 * ```tsx
 * const addShipping = useAddCartShippingMethod(cartId);
 *
 * addShipping.mutate({ optionId: "so_123", name: "Standard", amount: 12 });
 * ```
 */
export function useAddCartShippingMethod(cartId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: addShippingMethodMutationKey(cartId),
    mutationFn: ({ optionId }: AddCartShippingMethodInput) =>
      sdk.store.sellerCart.addShippingMethod(cartId, { option_id: optionId }),

    onMutate: async ({ optionId, name, amount }) => {
      const context = await snapshotSellerCarts(queryClient);
      queryClient.setQueryData<SalonStockerHttpTypes.StoreSellerCartsResponse>(
        queryKeys.sellerCarts.all,
        (old) =>
          old
            ? setCartShippingMethod(old, cartId, {
                shipping_option_id: optionId,
                name,
                amount
              })
            : old
      );
      return context;
    },

    onError: (_err, _vars, context) =>
      rollbackSellerCarts(queryClient, context),

    onSettled: () => invalidateSellerCarts(queryClient)
  });
}

/**
 * Adds a promo code to a seller cart.
 *
 * Each seller cart can have its own promo code(s) applied.
 * The promo code must be valid for the seller's promotions.
 *
 * @param cartId - The cart ID to add the promo code to
 *
 * @example
 * ```tsx
 * const addPromo = useAddCartPromoCode(cartId);
 *
 * const handleApply = async (code: string) => {
 *   try {
 *     await addPromo.mutateAsync(code);
 *     toast.success("Promo code applied!");
 *   } catch (error) {
 *     toast.error("Invalid promo code");
 *   }
 * };
 * ```
 */
export function useAddCartPromoCode(cartId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) =>
      sdk.store.sellerCart.addPromotions(cartId, { promo_codes: [code] }),

    onSuccess: () => {
      // A promotion moves the cart's totals, which can change which shipping
      // tier it qualifies for.
      invalidateCartShippingOptions(queryClient, cartId);
      invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Removes a promo code from a seller cart.
 *
 * @param cartId - The cart ID to remove the promo code from
 *
 * @example
 * ```tsx
 * const removePromo = useRemoveCartPromoCode(cartId);
 *
 * const handleRemove = async (code: string) => {
 *   await removePromo.mutateAsync(code);
 *   toast.success("Promo code removed");
 * };
 * ```
 */
export function useRemoveCartPromoCode(cartId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) =>
      sdk.store.sellerCart.removePromotions(cartId, { promo_codes: [code] }),

    onSuccess: () => {
      // A promotion moves the cart's totals, which can change which shipping
      // tier it qualifies for.
      invalidateCartShippingOptions(queryClient, cartId);
      invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Adds a promo code to any seller cart (cartId provided at mutation time).
 * Use this when you need to apply promo codes to multiple carts from a single component.
 *
 * @example
 * ```tsx
 * const addPromo = useApplyPromoCode();
 *
 * const handleApply = async (cartId: string, code: string) => {
 *   try {
 *     await addPromo.mutateAsync({ cartId, code });
 *     toast.success("Promo code applied!");
 *   } catch (error) {
 *     toast.error("Invalid promo code");
 *   }
 * };
 * ```
 */
export function useApplyPromoCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cartId, code }: { cartId: string; code: string }) =>
      sdk.store.sellerCart.addPromotions(cartId, { promo_codes: [code] }),

    onSuccess: (_data, { cartId }) => {
      // A promotion moves the cart's totals, which can change which shipping
      // tier it qualifies for.
      invalidateCartShippingOptions(queryClient, cartId);
      invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Removes a promo code from any seller cart (cartId provided at mutation time).
 * Use this when you need to remove promo codes from multiple carts from a single component.
 *
 * @example
 * ```tsx
 * const removePromo = useRemovePromoCode();
 *
 * const handleRemove = async (cartId: string, code: string) => {
 *   await removePromo.mutateAsync({ cartId, code });
 *   toast.success("Promo code removed");
 * };
 * ```
 */
export function useRemovePromoCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cartId, code }: { cartId: string; code: string }) =>
      sdk.store.sellerCart.removePromotions(cartId, { promo_codes: [code] }),

    onSuccess: (_data, { cartId }) => {
      // A promotion moves the cart's totals, which can change which shipping
      // tier it qualifies for.
      invalidateCartShippingOptions(queryClient, cartId);
      invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Address data for cart update (subset of full address)
 */
type CartAddressInput = {
  first_name?: string;
  last_name?: string;
  address_1?: string;
  address_2?: string;
  company?: string;
  city?: string;
  country_code?: string;
  province?: string;
  postal_code?: string;
  phone?: string;
};

/**
 * Input for updating multiple carts with address information.
 * All fields except cartIds are optional to support partial updates.
 */
export interface UpdateCartsAddressesInput {
  /** Cart IDs to update */
  cartIds: string[];
  /** Shipping address data (optional for partial updates) */
  shippingAddress?: CartAddressInput;
  /** Billing address data (optional for partial updates) */
  billingAddress?: CartAddressInput;
  /** Customer email (optional for partial updates) */
  email?: string;
}

/**
 * Updates shipping and billing addresses for multiple carts.
 * Used during checkout to apply the selected addresses to all carts being checked out.
 *
 * @example
 * ```tsx
 * const updateAddresses = useUpdateCartsAddresses();
 *
 * const handleContinue = async () => {
 *   await updateAddresses.mutateAsync({
 *     cartIds: ['cart_1', 'cart_2'],
 *     shippingAddress: {
 *       first_name: 'John',
 *       last_name: 'Doe',
 *       address_1: '123 Main St',
 *       city: 'Sydney',
 *       country_code: 'au',
 *       postal_code: '2000'
 *     },
 *     billingAddress: { ... },
 *     email: 'john@example.com'
 *   });
 *   navigate({ to: '/checkout', search: { step: 'shipping' } });
 * };
 * ```
 */
export function useUpdateCartsAddresses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cartIds,
      shippingAddress,
      billingAddress,
      email
    }: UpdateCartsAddressesInput) => {
      // Build update payload with only provided fields
      const updatePayload: HttpTypes.StoreUpdateCart = {};
      if (email !== undefined) {
        updatePayload.email = email;
      }
      if (shippingAddress !== undefined) {
        updatePayload.shipping_address =
          shippingAddress as HttpTypes.StoreUpdateCart["shipping_address"];
      }
      if (billingAddress !== undefined) {
        updatePayload.billing_address =
          billingAddress as HttpTypes.StoreUpdateCart["billing_address"];
      }

      // Update all carts in parallel
      const updates = cartIds.map((cartId) =>
        sdk.store.sellerCart.update(cartId, updatePayload)
      );

      return Promise.all(updates);
    },

    onSuccess: (_data, { cartIds }) => {
      // Where a cart ships to determines which options it is even offered, so
      // an address change invalidates the option list for every cart touched.
      for (const cartId of cartIds) {
        void invalidateCartShippingOptions(queryClient, cartId);
      }
      // Returned so the mutation settles once the refetch has landed: the
      // Payment step seeds its form from the cached cart as soon as the
      // Delivery step navigates, and needs the phone just written (KAN-255).
      return invalidateSellerCarts(queryClient);
    }
  });
}

/**
 * Fetches the external checkout URL for a seller cart - a redirect to the
 * seller's own store (external checkout), used for sellers where
 * `seller.supports_external_checkout` is true.
 *
 * This is a user-triggered action (a mutation, not a query): the caller invokes
 * it when the external-checkout interstitial opens and surfaces any `unmapped`
 * items. It is side-effect free - it does NOT mark the cart as handed off, so
 * merely opening the dialog never flips the cart to "sent". The handoff is
 * recorded separately by {@link useConfirmExternalCheckout} when the customer
 * confirms the redirect.
 *
 * @example
 * ```tsx
 * const { mutateAsync: getExternalCheckout, isPending } = useExternalCheckout();
 * const { url, unmapped, platform } = await getExternalCheckout(cart.cart_id);
 * ```
 */
export function useExternalCheckout() {
  return useMutation({
    mutationFn: (cartId: string) =>
      sdk.store.sellerCart.externalCheckout(cartId)
  });
}

/**
 * Confirms the external checkout handoff - marks the cart as sent to the
 * seller's store. Call this only when the customer actually confirms the
 * redirect ("Continue to <platform> checkout"), not when the interstitial
 * opens. The server stamps `handed_off_at`, so we refresh the cart queries on
 * success to reflect the "sent to supplier" state.
 *
 * @example
 * ```tsx
 * const { mutateAsync: confirmExternalCheckout } = useConfirmExternalCheckout();
 * await confirmExternalCheckout(cart.cart_id);
 * window.location.href = url;
 * ```
 */
export function useConfirmExternalCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cartId: string) =>
      sdk.store.sellerCart.confirmExternalCheckout(cartId),

    onSuccess: () => {
      // The handoff stamps handed_off_at on the cart; refresh so the UI can
      // reflect the "sent to supplier" state.
      invalidateSellerCarts(queryClient);
    }
  });
}
